import React, { useState, useEffect, useMemo, useRef } from "react";
import { useHistory } from "react-router-dom";
import { useSelector } from "react-redux";
import {
  Dialog,
  InputBase,
  List,
  ListItem,
  ListItemIcon,
  ListItemText,
  Typography,
  Box,
  makeStyles,
} from "@material-ui/core";
import SearchIcon from "@material-ui/icons/Search";
import {
  bottomRoutes,
  getNavRoutes,
} from "../../commons/ProjectsLayout/Routes/ProjectRoutes";

// Cmd/Ctrl+P quick tab switcher. Entries come from the same route config and
// permission filter as the sidebar (getNavRoutes + bottomRoutes), so it never
// lists a tab the user can't open from the nav.

const useStyles = makeStyles((theme) => ({
  container: { alignItems: "flex-start" },
  paper: {
    width: 600,
    maxWidth: "calc(100% - 32px)",
    margin: "12vh 16px 16px",
    borderRadius: 12,
    overflow: "hidden",
  },
  inputRow: {
    display: "flex",
    alignItems: "center",
    gap: 10,
    padding: "12px 16px",
    borderBottom: `1px solid ${theme.palette.divider}`,
  },
  input: { flex: 1, fontSize: 16 },
  list: { maxHeight: "50vh", overflowY: "auto", padding: "6px 0" },
  item: { paddingTop: 6, paddingBottom: 6 },
  selected: {
    backgroundColor: `${theme.palette.action.selected} !important`,
  },
  icon: { minWidth: 36, color: theme.palette.text.secondary },
  empty: { padding: "14px 16px", color: theme.palette.text.secondary },
  footer: {
    padding: "8px 16px",
    borderTop: `1px solid ${theme.palette.divider}`,
    color: theme.palette.text.secondary,
    fontSize: 12,
  },
}));

const escapeRegExp = (s) => s.replace(/[.*+?^${}()|[\]\\]/g, "\\$&");

// 0 = label prefix, 1 = word-start, 2 = substring anywhere; null = no match.
const matchRank = (label, query) => {
  const l = label.toLowerCase();
  if (l.startsWith(query)) return 0;
  if (new RegExp(`(^|[^a-z0-9])${escapeRegExp(query)}`).test(l)) return 1;
  if (l.includes(query)) return 2;
  return null;
};

const isShortcut = (e) =>
  (e.metaKey || e.ctrlKey) &&
  !e.altKey &&
  !e.shiftKey &&
  (e.key || "").toLowerCase() === "p";

function TabSwitcher() {
  const classes = useStyles();
  const history = useHistory();
  const permissions = useSelector((state) => state.login.permissions);

  const [open, setOpen] = useState(false);
  const [query, setQuery] = useState("");
  const [highlight, setHighlight] = useState(0);
  const itemRefs = useRef([]);

  const entries = useMemo(() => {
    const seen = new Set();
    return [...getNavRoutes(permissions), ...bottomRoutes].filter((route) => {
      // The sidebar hides CRM ("/crm" alone has no page); mirror that.
      if (route.name === "CRM" || seen.has(route.to)) return false;
      seen.add(route.to);
      return true;
    });
  }, [permissions]);

  const results = useMemo(() => {
    const q = query.trim().toLowerCase();
    if (!q) return entries;
    return entries
      .map((route, index) => ({ route, index, rank: matchRank(route.name, q) }))
      .filter((r) => r.rank !== null)
      .sort((a, b) => a.rank - b.rank || a.index - b.index)
      .map((r) => r.route);
  }, [entries, query]);

  useEffect(() => {
    const onKeyDown = (e) => {
      if (!isShortcut(e)) return;
      // Always suppress the browser print dialog while logged in.
      e.preventDefault();
      setOpen((wasOpen) => {
        if (wasOpen) return false;
        // Don't stack on top of another open dialog.
        if (document.querySelector(".MuiDialog-root")) return false;
        return true;
      });
    };
    window.addEventListener("keydown", onKeyDown, true);
    return () => window.removeEventListener("keydown", onKeyDown, true);
  }, []);

  useEffect(() => {
    if (open) {
      setQuery("");
      setHighlight(0);
    }
  }, [open]);

  useEffect(() => {
    setHighlight(0);
  }, [query]);

  useEffect(() => {
    const el = itemRefs.current[highlight];
    if (el && el.scrollIntoView) el.scrollIntoView({ block: "nearest" });
  }, [highlight, results]);

  const go = (route) => {
    if (!route) return;
    setOpen(false);
    history.push(route.to);
  };

  const onInputKeyDown = (e) => {
    if (!results.length) return;
    if (e.key === "ArrowDown") {
      e.preventDefault();
      setHighlight((h) => (h + 1) % results.length);
    } else if (e.key === "ArrowUp") {
      e.preventDefault();
      setHighlight((h) => (h - 1 + results.length) % results.length);
    } else if (e.key === "Enter") {
      e.preventDefault();
      go(results[highlight]);
    }
  };

  return (
    <Dialog
      open={open}
      onClose={() => setOpen(false)}
      classes={{ container: classes.container, paper: classes.paper }}
      maxWidth={false}
    >
      <Box className={classes.inputRow}>
        <SearchIcon color="action" />
        <InputBase
          autoFocus
          className={classes.input}
          placeholder="Jump to tab…"
          value={query}
          onChange={(e) => setQuery(e.target.value)}
          onKeyDown={onInputKeyDown}
          inputProps={{ "aria-label": "Search tabs" }}
        />
      </Box>
      <List className={classes.list} dense>
        {results.length === 0 ? (
          <ListItem className={classes.empty}>
            <Typography variant="body2">No matching tabs</Typography>
          </ListItem>
        ) : (
          results.map((route, i) => (
            <ListItem
              key={route.to}
              button
              ref={(el) => (itemRefs.current[i] = el)}
              selected={i === highlight}
              classes={{ root: classes.item, selected: classes.selected }}
              onMouseEnter={() => setHighlight(i)}
              onClick={() => go(route)}
            >
              {route.icon ? (
                <ListItemIcon className={classes.icon}>{route.icon}</ListItemIcon>
              ) : null}
              <ListItemText primary={route.name} secondary={route.to} />
            </ListItem>
          ))
        )}
      </List>
      <Box className={classes.footer}>
        ↑↓ to navigate · Enter to open · Esc to close
      </Box>
    </Dialog>
  );
}

export default TabSwitcher;
